import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import DateNavigation from './components/DateNavigation';
import WellnessSlider from './components/WellnessSlider';
import HooperIndexDisplay from './components/HooperIndexDisplay';
import WellnessTrendChart from './components/WellnessTrendChart';
import WellnessHistory from './components/WellnessHistory';
import WellnessCSVImport from './components/WellnessCSVImport';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import wellnessService from '../../services/wellnessService';
import ContactButton from '../../components/ContactButton';
import EditWellnessModal from './components/EditWellnessModal';

const DailyWellness = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [wellnessData, setWellnessData] = useState({
    sleep: 4,
    fatigue: 4,
    soreness: 3,
    stress: 3
  });
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Real data from Supabase (removed mock data)
  const [wellnessHistory, setWellnessHistory] = useState([]);
  const [trendData, setTrendData] = useState([]);

  const wellnessSliderConfigs = [
    {
      key: 'sleep',
      label: 'Sleep Quality',
      icon: 'Moon',
      description: 'How well did you sleep last night?',
      anchors: ['Excellent', 'Average', 'Very Poor']
    },
    {
      key: 'fatigue',
      label: 'Fatigue Levels',
      icon: 'Battery',
      description: 'How tired do you feel right now?',
      anchors: ['Very Fresh', 'Moderate', 'Exhausted']
    },
    {
      key: 'soreness',
      label: 'Muscle Soreness',
      icon: 'Zap',
      description: 'How sore are your muscles today?',
      anchors: ['No Soreness', 'Some Soreness', 'Very Sore']
    },
    {
      key: 'stress',
      label: 'Stress Levels',
      icon: 'Brain',
      description: 'How stressed do you feel today?',
      anchors: ['Very Relaxed', 'Moderate', 'Very Stressed']
    }
  ];

  // Load wellness data on component mount
  useEffect(() => {
    loadWellnessData();
  }, []);

  // Load wellness entry for selected date
  useEffect(() => {
    loadWellnessForDate(selectedDate);
  }, [selectedDate]);

  const loadWellnessData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load all wellness entries
      const entries = await wellnessService?.getAll();
      setWellnessHistory(entries);
      
      // Get last 30 days for trend chart
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      const trends = await wellnessService?.getByDateRange(
        thirtyDaysAgo?.toISOString()?.split('T')?.[0],
        new Date()?.toISOString()?.split('T')?.[0]
      );
      setTrendData(trends);
      
      // Generate insights based on recent data
      // generateInsights(entries?.slice(0, 7)); // Last 7 entries - REMOVED
      
    } catch (err) {
      setError(err?.message);
      console.error('Failed to load wellness data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadWellnessForDate = async (date) => {
    try {
      const dateString = date?.toISOString()?.split('T')?.[0];
      const entry = await wellnessService?.getByDate(dateString);
      
      if (entry) {
        setWellnessData({
          sleep: entry?.sleep,
          fatigue: entry?.fatigue,
          soreness: entry?.soreness,
          stress: entry?.stress
        });
        setNotes(entry?.notes || '');
      } else {
        // Reset to defaults for new entry
        setWellnessData({
          sleep: 4,
          fatigue: 4,
          soreness: 3,
          stress: 3
        });
        setNotes('');
      }
    } catch (err) {
      console.error('Failed to load wellness entry for date:', err);
    }
  };

  const calculateHooperIndex = () => {
    return wellnessData?.sleep + wellnessData?.fatigue + wellnessData?.soreness + wellnessData?.stress;
  };

  const getHistoricalAverage = () => {
    if (wellnessHistory?.length === 0) return null;
    const sum = wellnessHistory?.reduce((acc, entry) => acc + entry?.hooperIndex, 0);
    return sum / wellnessHistory?.length;
  };

  const handleWellnessChange = (component, value) => {
    setWellnessData(prev => ({
      ...prev,
      [component]: value
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      const wellnessEntry = {
        date: selectedDate?.toISOString()?.split('T')?.[0],
        sleep: wellnessData?.sleep,
        fatigue: wellnessData?.fatigue,
        soreness: wellnessData?.soreness,
        stress: wellnessData?.stress,
        notes: notes?.trim() || null
      };
      
      await wellnessService?.createOrUpdate(wellnessEntry);
      
      // Reload data to get updated lists
      await loadWellnessData();
      
    } catch (err) {
      setError(err?.message);
      console.error('Failed to save wellness entry:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedEntry) => {
    try {
      setIsSaving(true);
      setError('');
      
      const wellnessEntry = {
        date: updatedEntry?.date,
        sleep: updatedEntry?.sleep,
        fatigue: updatedEntry?.fatigue,
        soreness: updatedEntry?.soreness,
        stress: updatedEntry?.stress,
        notes: updatedEntry?.notes?.trim() || null
      };
      
      await wellnessService?.createOrUpdate(wellnessEntry);
      
      // Reload data to get updated lists
      await loadWellnessData();
      
      // Close modal
      setShowEditModal(false);
      setEditingEntry(null);
      
    } catch (err) {
      setError(err?.message);
      console.error('Failed to update wellness entry:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setIsLoading(true);
      setError('');

      await wellnessService?.deleteAll();

      // Reload wellness data
      await loadWellnessData();

      setNotification({
        type: 'success',
        message: 'All wellness entries deleted successfully',
        duration: 3000
      });
    } catch (error) {
      setError(error?.message || 'Failed to delete all wellness entries');
      setNotification({
        type: 'error',
        message: error?.message || 'Failed to delete all wellness entries',
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const csvContent = await wellnessService?.exportToCSV();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wellness-data-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      a?.click();
      window.URL?.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to export data: ${err?.message}`);
    }
  };

  const handleImport = async (csvData) => {
    try {
      setError('');
      await wellnessService?.importFromCSV(csvData);
      await loadWellnessData(); // Reload data
      setShowImportModal(false);
    } catch (err) {
      setError(`Failed to import data: ${err?.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="text-text-secondary">Loading wellness data...</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Daily Wellness - Sports Science Platform</title>
        <meta name="description" content="Track your daily wellness metrics using the Hooper Index to optimize recovery and performance." />
      </Helmet>
      <Header />
      <main className="pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                <div>
                  <h4 className="text-sm font-medium text-error mb-1">Error</h4>
                  <p className="text-sm text-text-secondary">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">Daily Wellness</h1>
                <p className="text-text-secondary">
                  Track your wellness using the Hooper index methodology to monitor recovery and training readiness.
                </p>
              </div>
              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                <Button
                  variant="outline"
                  onClick={handleExport}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export Data
                </Button>
              </div>
            </div>
          </div>

          {/* Date Navigation */}
          <DateNavigation
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            streak={wellnessHistory?.length}
            className="mb-8"
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            {/* Left Column - Wellness Entry */}
            <div className="xl:col-span-8 space-y-8">
              {/* Wellness Sliders */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {wellnessSliderConfigs?.map((config) => (
                  <WellnessSlider
                    key={config?.key}
                    label={config?.label}
                    value={wellnessData?.[config?.key]}
                    onChange={(value) => handleWellnessChange(config?.key, value)}
                    icon={config?.icon}
                    description={config?.description}
                    anchors={config?.anchors}
                  />
                ))}
              </div>

              {/* Notes Section */}
              <div className="bg-card rounded-lg p-6 border border-border">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="FileText" size={20} color="var(--color-accent)" strokeWidth={2} />
                  <h3 className="text-lg font-semibold text-text-primary">Notes</h3>
                </div>
                <Input
                  type="text"
                  placeholder="Add any additional notes about your wellness today..."
                  value={notes}
                  onChange={(e) => setNotes(e?.target?.value)}
                  description="Optional qualitative observations about your wellness state"
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  loading={isSaving}
                  iconName="Save"
                  iconPosition="left"
                  size="lg"
                >
                  Save Wellness Entry
                </Button>
              </div>
            </div>

            {/* Right Column - Analytics */}
            <div className="xl:col-span-4 space-y-8">
              {/* Hooper Index Display */}
              <HooperIndexDisplay
                currentScore={calculateHooperIndex()}
                historicalAverage={getHistoricalAverage()}
                sleep={wellnessData?.sleep}
                fatigue={wellnessData?.fatigue}
                soreness={wellnessData?.soreness}
                stress={wellnessData?.stress}
              />
            </div>
          </div>

          {/* Bottom Section - Trends and History */}
          <div className="mt-12 space-y-8">
            {/* Wellness Trend Chart */}
            <WellnessTrendChart data={trendData} />

            {/* Wellness History */}
            <WellnessHistory
              entries={wellnessHistory}
              onEdit={handleEdit}
              onDeleteAll={handleDeleteAll}
              onImport={() => setShowImportModal(true)}
              onExport={handleExport}
              className="mb-8"
            />
          </div>

          {/* CSV Import Modal */}
          {showImportModal && (
            <WellnessCSVImport
              onImport={handleImport}
              onClose={() => setShowImportModal(false)}
            />
          )}

          {/* Edit Wellness Modal */}
          {showEditModal && editingEntry && (
            <EditWellnessModal
              entry={editingEntry}
              onSave={handleSaveEdit}
              onClose={() => {
                setShowEditModal(false);
                setEditingEntry(null);
              }}
              isSaving={isSaving}
            />
          )}
        </div>
      </main>
      <ContactButton />
    </div>
  );
};

export default DailyWellness;